// frontend/src/pages/DocumentosLegalesPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import bodegaService from '../services/bodegaService';
import eventosGanttService from '../services/eventosGanttService';
import { ordenCompraService, proveedorService, proyectoService, recursoService } from '../services/onebaseService';
import tesoreriaService from '../services/tesoreriaService';
import documentosLegalesService from '../services/documentosLegalesService';
import { FiPlus, FiEdit2, FiTrash2, FiSettings, FiCalendar, FiList, FiFilter, FiDownload } from 'react-icons/fi';
import { DndContext, DragOverlay, closestCorners, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import CrearEditarDocumentoLegalModal from '../components/CrearEditarDocumentoLegalModal';
import CrearOrdenCompraOneBaseModal from '../components/CrearOrdenCompraOneBaseModal';
import DocumentoLegalKanbanColumn from '../components/DocumentoLegalKanbanColumn';
import DocumentoLegalCard from '../components/DocumentoLegalCard';
import DocumentoLegalDetailPanel from '../components/DocumentoLegalDetailPanel';
import Loading from '../components/Loading';
import './DocumentosLegalesPage.css';

const TABS = [
  { id: 'gantt',       label: '📊 Gantt / Recursos' },
  { id: 'solicitudes', label: 'Solicitudes' },
  { id: 'ordenes', label: 'Ordenes de Compra' },
  { id: 'guias', label: 'Guias de Despacho' },
  { id: 'documentos', label: 'Documentos Legales' },
  { id: 'calendario', label: 'Calendario' },
  { id: 'cronograma', label: 'Cronograma' },
];

const COLUMNS = [
  { id: 'borrador', label: 'Borrador', color: '#6b7280', bgColor: '#f3f4f6', icon: '\uD83D\uDCDD' },
  { id: 'emitida',  label: 'Emitida',  color: '#3b82f6', bgColor: '#dbeafe', icon: '\uD83D\uDCE4' },
  { id: 'pagada',   label: 'Pagada',   color: '#10b981', bgColor: '#d1fae5', icon: '\u2705' },
  { id: 'vencida',  label: 'Vencida',  color: '#f59e0b', bgColor: '#fef3c7', icon: '\u23F0' },
  { id: 'anulada',  label: 'Anulada',  color: '#ef4444', bgColor: '#fee2e2', icon: '\uD83D\uDEAB' },
];

const SOL_COLUMNS = [
  { id: 'pendiente',  label: 'Pendiente',  color: '#f59e0b', bgColor: '#fef3c7', icon: '\u23F3' },
  { id: 'aprobada',   label: 'Aprobada',   color: '#10b981', bgColor: '#d1fae5', icon: '\u2705' },
  { id: 'rechazada',  label: 'Rechazada',  color: '#ef4444', bgColor: '#fee2e2', icon: '\u2716' },
  { id: 'cancelada',  label: 'Cancelada',  color: '#6b7280', bgColor: '#f3f4f6', icon: '\uD83D\uDEAB' },
];

const OC_COLUMNS = [
  { id: 'pendiente',  label: 'Pendiente',  color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' },
  { id: 'emitida',   label: 'Emitida',    color: '#3b82f6', bgColor: '#dbeafe', icon: '📤' },
  { id: 'recibida',  label: 'Recibida',   color: '#10b981', bgColor: '#d1fae5', icon: '📦' },
  { id: 'facturada', label: 'Facturada',  color: '#8b5cf6', bgColor: '#ede9fe', icon: '🧾' },
  { id: 'cancelada', label: 'Cancelada',  color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' },
];

const GUIA_COLUMNS = [
  { id: 'borrador',      label: 'Borrador',      color: '#6b7280', bgColor: '#f3f4f6', icon: '📝' },
  { id: 'recepcionado',  label: 'Recepcionado',  color: '#10b981', bgColor: '#d1fae5', icon: '✅' },
  { id: 'anulado',       label: 'Anulado',       color: '#ef4444', bgColor: '#fee2e2', icon: '❌' },
];

const ALLOWED_TRANSITIONS = {
  borrador: ['emitida', 'anulada'],
  emitida:  ['pagada', 'vencida', 'anulada'],
  pagada:   [],
  vencida:  ['pagada', 'anulada'],
  anulada:  [],
};

const getApiBaseUrl = () => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  return baseURL.replace(/\/content\/?$/i, '/contabilidad');
};

const ensureArray = (v) => Array.isArray(v) ? v : [];

// ============================================================
// SOLICITUDES TAB
// ============================================================
const SolicitudesTab = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroBuscar, setFiltroBuscar] = useState('');
  const [showCrearSolicitud, setShowCrearSolicitud] = useState(false);
  const [formSolicitud, setFormSolicitud] = useState({ proyecto: '', tarea_evento: '', bodega: '', observaciones_solicitud: '' });
  const [asignarTareas, setAsignarTareas] = useState(false);
  const [savingSolicitud, setSavingSolicitud] = useState(false);
  const [proyectosListSol, setProyectosListSol] = useState([]);
  const [tareaEventosFiltradas, setTareaEventosFiltradas] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  const [bodegasListSol, setBodegasListSol] = useState([]);
  const [recursosListSol, setRecursosListSol] = useState([]);
  const [recursosDetalle, setRecursosDetalle] = useState([]);

  // Estados para modal OC desde solicitud
  const [showOCModal, setShowOCModal] = useState(false);
  const [solicitudParaOC, setSolicitudParaOC] = useState(null);
  const [presupuestoDataOC, setPresupuestoDataOC] = useState(null);
  const [itemsSeleccionadosOC, setItemsSeleccionadosOC] = useState([]);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await bodegaService.getSolicitudesRecurso();
      setSolicitudes(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar solicitudes'); }
    finally { setLoading(false); }
  };

  const handleAprobar = async (id) => {
    if (!window.confirm('¿Aprobar esta solicitud?')) return;
    try {
      await bodegaService.aprobarSolicitudRecurso(id);
      toast.success('Solicitud aprobada');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || err.response?.data?.error || 'Error al aprobar'); }
  };

  const handleRechazar = async (id) => {
    if (!window.confirm('¿Rechazar esta solicitud?')) return;
    try {
      await bodegaService.rechazarSolicitudRecurso(id);
      toast.success('Solicitud rechazada');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al rechazar'); }
  };

  const handleAbrirCrearSolicitud = async () => {
    setFormSolicitud({ proyecto: '', tarea_evento: '', bodega: '', observaciones_solicitud: '' });
    setAsignarTareas(false);
    setRecursosDetalle([]);
    setTareaEventosFiltradas([]);
    setShowCrearSolicitud(true);
    try {
      const [proyectosRes, bodegasRes, recursosRes] = await Promise.all([
        proyectosListSol.length === 0 ? proyectoService.getAll() : Promise.resolve(null),
        bodegasListSol.length === 0 ? bodegaService.getBodegas() : Promise.resolve(null),
        recursosListSol.length === 0 ? recursoService.getAll() : Promise.resolve(null),
      ]);
      if (proyectosRes) setProyectosListSol(ensureArray(proyectosRes.data.results || proyectosRes.data));
      if (bodegasRes) setBodegasListSol(ensureArray(bodegasRes.data.results || bodegasRes.data));
      if (recursosRes) setRecursosListSol(ensureArray(recursosRes.data.results || recursosRes.data));
    } catch { /* ignore */ }
  };

  const handleProyectoChange = async (proyectoId) => {
    setFormSolicitud(f => ({ ...f, proyecto: proyectoId, tarea_evento: '' }));
    setTareaEventosFiltradas([]);
    if (!proyectoId) return;
    try {
      setLoadingTareas(true);
      const eventosBaseUrl = (api?.defaults?.baseURL || '/api/content').replace(/\/content\/?$/i, '/eventos');
      const res = await api.get(`${eventosBaseUrl}/tareas/`, { params: { evento__proyecto: proyectoId } });
      setTareaEventosFiltradas(ensureArray(res.data.results || res.data));
    } catch { /* ignore */ }
    finally { setLoadingTareas(false); }
  };

  const handleAsignarTareasToggle = () => {
    setAsignarTareas(prev => {
      if (prev) setFormSolicitud(f => ({ ...f, tarea_evento: '' }));
      return !prev;
    });
  };

  const handleAgregarRecurso = () => {
    setRecursosDetalle(prev => [...prev, { recurso: '', cantidad_solicitada: 1 }]);
  };

  const handleRecursoChange = (index, field, value) => {
    setRecursosDetalle(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const handleEliminarRecurso = (index) => {
    setRecursosDetalle(prev => prev.filter((_, i) => i !== index));
  };

  const handleAbrirOCDesdeSolicitud = (solicitud) => {
    const primerDetalleConPresupuesto = solicitud.detalles?.find(d => d.recurso_data?.presupuesto_costo_id);
    const presupuestoCostoId = primerDetalleConPresupuesto?.recurso_data?.presupuesto_costo_id || 0;

    const presupuestoSimulado = {
      id: presupuestoCostoId,
      proyecto: solicitud.proyecto_data?.id || 0,
      proyecto_nombre: solicitud.proyecto_data?.nombre || 'N/A',
      version: 1,
    };

    const items = solicitud.detalles?.map(detalle => ({
      id: detalle.presupuesto_costo_recurso || detalle.recurso_data?.id || detalle.id,
      nombre: detalle.recurso_data?.nombre || 'Recurso',
      unidad: detalle.recurso_data?.unidad_medida || 'Un',
      cantidad_presupuestada_costo: detalle.recurso_data?.cantidad_presupuestada_costo || detalle.recurso_data?.cantidad_presupuestada || detalle.cantidad_solicitada,
      cantidad_presupuesto: detalle.recurso_data?.cantidad_presupuesto || detalle.recurso_data?.cantidad_presupuestada || detalle.cantidad_solicitada,
      cantidad_solicitada: detalle.recurso_data?.cantidad_solicitada || 0,
      cantidad_solicitada_oc: detalle.recurso_data?.cantidad_solicitada_oc || 0,
      cantidad_salidas_bodega: detalle.recurso_data?.cantidad_salidas_bodega || 0,
      cantidad_disponible: detalle.recurso_data?.cantidad_disponible || 0,
      precio_unitario_costo: detalle.recurso_data?.precio_unitario || 0,
      recurso_referencia: detalle.recurso,
      recurso_referencia_data: { ...detalle.recurso_data, stock: false },
      area_id: detalle.recurso_data?.area_id || null,
      partida_id: detalle.recurso_data?.partida_id || null,
      area_nombre: detalle.recurso_data?.area_nombre || 'Solicitud de Compra',
      partida_nombre: detalle.recurso_data?.partida_nombre || `Solicitud #${solicitud.id}`,
      cantidad_dias: 1,
      cantidad_inicial: detalle.cantidad || 0,
    })) || [];

    setSolicitudParaOC(solicitud);
    setPresupuestoDataOC(presupuestoSimulado);
    setItemsSeleccionadosOC(items);
    setShowOCModal(true);
  };

  const handleCerrarOCModal = () => {
    setShowOCModal(false);
    setSolicitudParaOC(null);
    setPresupuestoDataOC(null);
    setItemsSeleccionadosOC([]);
  };

  const handleOCCreadaDesdeSolicitud = async () => {
    try {
      if (solicitudParaOC) {
        await bodegaService.aprobarSolicitudRecurso(solicitudParaOC.id);
        toast.success('Orden de Compra creada y solicitud aprobada');
        cargar();
      }
    } catch {
      toast.warning('OC creada, pero no se pudo aprobar la solicitud automáticamente');
      cargar();
    } finally {
      handleCerrarOCModal();
    }
  };

  const handleGuardarSolicitud = async () => {
    if (!formSolicitud.proyecto) { toast.error('Debe seleccionar un Proyecto'); return; }
    const detallesValidos = recursosDetalle.filter(r => r.recurso && Number(r.cantidad_solicitada) > 0);
    if (detallesValidos.length === 0) { toast.error('Debe agregar al menos un recurso con cantidad'); return; }
    try {
      setSavingSolicitud(true);
      const data = { proyecto: formSolicitud.proyecto };
      if (asignarTareas && formSolicitud.tarea_evento) data.tarea_evento = formSolicitud.tarea_evento;
      if (formSolicitud.bodega) data.bodega = formSolicitud.bodega;
      if (formSolicitud.observaciones_solicitud) data.observaciones_solicitud = formSolicitud.observaciones_solicitud;
      data.detalles_data = detallesValidos.map(r => ({
        recurso: r.recurso,
        cantidad_solicitada: Number(r.cantidad_solicitada),
      }));
      await bodegaService.createSolicitudRecurso(data);
      toast.success('Solicitud creada exitosamente');
      setShowCrearSolicitud(false);
      cargar();
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        toast.error(msgs);
      } else { toast.error('Error al crear solicitud'); }
    } finally { setSavingSolicitud(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CL') : '-';

  const solicitudesFiltradas = useMemo(() => {
    if (!filtroBuscar) return solicitudes;
    const t = filtroBuscar.toLowerCase();
    return solicitudes.filter(s =>
      (s.proyecto_data?.nombre || '').toLowerCase().includes(t) ||
      (s.tarea_evento_titulo || '').toLowerCase().includes(t) ||
      (s.solicitado_por_nombre || '').toLowerCase().includes(t) ||
      String(s.id).includes(t)
    );
  }, [solicitudes, filtroBuscar]);

  const solicitudesPorEstado = useMemo(() => {
    const grouped = {};
    SOL_COLUMNS.forEach(col => { grouped[col.id] = []; });
    solicitudesFiltradas.forEach(s => {
      if (grouped[s.estado]) grouped[s.estado].push(s);
    });
    return grouped;
  }, [solicitudesFiltradas]);

  if (loading) return <Loading />;

  return (
    <>
      <div className="filtros-container">
        <input
          className="input-filtro"
          placeholder="Buscar por proyecto, tarea o solicitante..."
          value={filtroBuscar}
          onChange={e => setFiltroBuscar(e.target.value)}
        />
        <button className="btn-primary" onClick={handleAbrirCrearSolicitud}><FiPlus /> Nueva Solicitud</button>
      </div>

      {/* ── Kanban board ── */}
      <div className="docleg-kanban-board">
        {SOL_COLUMNS.map(col => {
          const items = solicitudesPorEstado[col.id] || [];
          return (
            <div key={col.id} className="docleg-kanban-column">
              <div className="docleg-column-header" style={{ borderTopColor: col.color }}>
                <div className="docleg-column-title">
                  <span>{col.icon}</span>
                  <span>{col.label}</span>
                  <span className="docleg-column-count">{items.length}</span>
                </div>
              </div>
              <div className="docleg-column-content">
                {items.length === 0 ? (
                  <div className="docleg-column-empty">Sin solicitudes</div>
                ) : items.map(s => (
                  <div key={s.id} className="docleg-card" style={{ cursor: 'default' }}>
                    {/* Header: ID + proyecto */}
                    <div className="docleg-card-header">
                      <span className="docleg-card-numero">#{s.id}</span>
                      {s.proyecto_data?.nombre && (
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '1px 6px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.proyecto_data.nombre}
                        </span>
                      )}
                    </div>

                    {/* Tarea/Evento */}
                    {s.tarea_evento_titulo && (
                      <div className="docleg-card-body" style={{ color: '#4b5563' }}>
                        📋 {s.tarea_evento_titulo}
                      </div>
                    )}

                    {/* Recursos */}
                    {s.detalles && s.detalles.length > 0 && (
                      <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {s.detalles.map((d, i) => (
                          <span key={i}>
                            • {d.recurso_data?.nombre || `Recurso #${d.recurso}`}
                            {d.cantidad_solicitada ? ` × ${d.cantidad_solicitada}` : ''}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* OC asociada */}
                    {s.orden_compra && (
                      <div style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>
                        OC #{s.orden_compra}
                      </div>
                    )}

                    {/* Footer: solicitante + fecha + acciones */}
                    <div className="docleg-card-footer">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {s.solicitado_por_nombre && (
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{s.solicitado_por_nombre}</span>
                        )}
                        <span className="docleg-card-date">{formatDate(s.fecha_solicitud)}</span>
                      </div>
                      <div className="docleg-card-actions">
                        {s.estado === 'pendiente' && (
                          <>
                            <button
                              className="btn-icon btn-icon-sm"
                              style={{ color: '#065f46', background: '#d1fae5' }}
                              onClick={() => handleAprobar(s.id)}
                              title="Aprobar"
                            >✓</button>
                            <button
                              className="btn-icon btn-icon-sm btn-danger"
                              onClick={() => handleRechazar(s.id)}
                              title="Rechazar"
                            >✗</button>
                          </>
                        )}
                        {!s.orden_compra && (
                          <button
                            className="btn-icon btn-icon-sm"
                            style={{ color: '#4338ca', background: '#e0e7ff', fontSize: 10, fontWeight: 700, padding: '0 4px', width: 'auto' }}
                            onClick={() => handleAbrirOCDesdeSolicitud(s)}
                            title="Crear Orden de Compra"
                          >+OC</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {showCrearSolicitud && (
        <div className="pago-modal-overlay" onClick={() => setShowCrearSolicitud(false)}>
          <div className="pago-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="pago-modal-header">
              <h3>Nueva Solicitud de Recurso</h3>
              <button className="btn-icon" onClick={() => setShowCrearSolicitud(false)}>&times;</button>
            </div>
            <div className="pago-modal-body">
              <div className="pago-form-grid">

                {/* Proyecto */}
                <div className="pago-form-group pago-form-full">
                  <label>Proyecto *</label>
                  <select value={formSolicitud.proyecto} onChange={e => handleProyectoChange(e.target.value)}>
                    <option value="">Seleccionar proyecto...</option>
                    {proyectosListSol.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Switch Asignar Tareas */}
                {formSolicitud.proyecto && (
                  <div className="pago-form-group pago-form-full" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ margin: 0 }}>Asignar tarea?</label>
                    <label className="sol-switch">
                      <input type="checkbox" checked={asignarTareas} onChange={handleAsignarTareasToggle} />
                      <span className="sol-switch-slider" />
                    </label>
                  </div>
                )}

                {/* Tarea / Evento (condicional) */}
                {formSolicitud.proyecto && asignarTareas && (
                  <div className="pago-form-group pago-form-full">
                    <label>Tarea / Evento</label>
                    {loadingTareas ? (
                      <select disabled><option>Cargando tareas...</option></select>
                    ) : (
                      <select value={formSolicitud.tarea_evento} onChange={e => setFormSolicitud(f => ({ ...f, tarea_evento: e.target.value }))}>
                        <option value="">Seleccionar tarea o evento...</option>
                        {tareaEventosFiltradas.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.titulo || t.nombre || `Tarea #${t.id}`}
                            {t.evento_titulo ? ` — ${t.evento_titulo}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {!loadingTareas && tareaEventosFiltradas.length === 0 && (
                      <small style={{ color: '#6b7280' }}>No hay tareas para este proyecto</small>
                    )}
                  </div>
                )}

                {/* Bodega */}
                <div className="pago-form-group">
                  <label>Bodega de Recepcion</label>
                  <select value={formSolicitud.bodega} onChange={e => setFormSolicitud(f => ({ ...f, bodega: e.target.value }))}>
                    <option value="">Sin bodega asignada</option>
                    {bodegasListSol.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select>
                </div>

                {/* Observaciones */}
                <div className="pago-form-group pago-form-full">
                  <label>Observaciones</label>
                  <textarea
                    value={formSolicitud.observaciones_solicitud}
                    onChange={e => setFormSolicitud(f => ({ ...f, observaciones_solicitud: e.target.value }))}
                    rows={2}
                    placeholder="Motivo o detalle de la solicitud..."
                  />
                </div>

                {/* Recursos */}
                <div className="pago-form-group pago-form-full">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ margin: 0 }}>Recursos solicitados *</label>
                    <button type="button" className="btn-primary" style={{ padding: '4px 12px', fontSize: 13 }} onClick={handleAgregarRecurso}>
                      <FiPlus /> Agregar recurso
                    </button>
                  </div>
                  {recursosDetalle.length === 0 ? (
                    <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>Agrega al menos un recurso con su cantidad requerida.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Recurso</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, width: 100 }}>Cantidad</th>
                          <th style={{ width: 36 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {recursosDetalle.map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '4px 8px' }}>
                              <select
                                value={r.recurso}
                                onChange={e => handleRecursoChange(i, 'recurso', e.target.value)}
                                style={{ width: '100%' }}
                              >
                                <option value="">Seleccionar recurso...</option>
                                {recursosListSol.map(rec => (
                                  <option key={rec.id} value={rec.id}>{rec.nombre}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '4px 8px' }}>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={r.cantidad_solicitada}
                                onChange={e => handleRecursoChange(i, 'cantidad_solicitada', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleEliminarRecurso(i)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                              >&times;</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

              </div>
            </div>
            <div className="pago-modal-footer">
              <button className="btn-secondary" onClick={() => setShowCrearSolicitud(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleGuardarSolicitud} disabled={savingSolicitud}>
                {savingSolicitud ? 'Creando...' : 'Crear Solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showOCModal && presupuestoDataOC && (
        <CrearOrdenCompraOneBaseModal
          presupuesto={presupuestoDataOC}
          itemsSeleccionados={itemsSeleccionadosOC}
          onClose={handleCerrarOCModal}
          onCreated={handleOCCreadaDesdeSolicitud}
        />
      )}
    </>
  );
};

// ============================================================
// GANTT RECURSOS TAB
// ============================================================
const HORIZONTE_OPTS = [
  { value: 15,  label: '15 días' },
  { value: 30,  label: '30 días' },
  { value: 60,  label: '60 días' },
  { value: 90,  label: '90 días' },
];

const UrgenciaBadge = ({ dias }) => {
  if (dias === null || dias === undefined) return null;
  if (dias < 0)  return <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>Vencida</span>;
  if (dias === 0) return <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>Hoy</span>;
  if (dias <= 3)  return <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>{dias}d</span>;
  if (dias <= 7)  return <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>{dias}d</span>;
  return <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>{dias}d</span>;
};

const GanttRecursosTab = () => {
  const [horizonte, setHorizonte] = useState(30);
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [buscar, setBuscar] = useState('');
  const [expandidos, setExpandidos] = useState({});

  // Selección para solicitud
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal crear solicitud
  const [showSolModal, setShowSolModal] = useState(false);
  const [formSol, setFormSol] = useState({ proyecto: '', bodega: '', observaciones: '' });
  const [cantidades, setCantidades] = useState({});
  const [proyectosListG, setProyectosListG] = useState([]);
  const [bodegasListG, setBodegasListG] = useState([]);
  const [savingSol, setSavingSol] = useState(false);

  useEffect(() => { cargar(); }, [horizonte]);

  const cargar = async () => {
    try {
      setLoading(true);
      setReporte(null);
      setSelectedIds(new Set());
      const res = await eventosGanttService.tareas.validarStockRecursos({ dias: horizonte });
      setReporte(res.data);
    } catch {
      toast.error('Error al cargar reporte de recursos Gantt');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpandir = (id) => setExpandidos(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const recursos = reporte?.recursos || [];
  const filtrados = recursos.filter(r => {
    if (filtro === 'solicitar' && !r.requiere_solicitud) return false;
    if (filtro === 'ok' && r.requiere_solicitud) return false;
    if (buscar && !r.recurso_nombre.toLowerCase().includes(buscar.toLowerCase())) return false;
    return true;
  }).sort((a, b) => (a.dias_hasta_primera_tarea ?? 9999) - (b.dias_hasta_primera_tarea ?? 9999));

  const allFilteredSelected = filtrados.length > 0 && filtrados.every(r => selectedIds.has(r.recurso_id));
  const someFilteredSelected = filtrados.some(r => selectedIds.has(r.recurso_id));

  const toggleSelectAll = (e) => {
    e.stopPropagation();
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtrados.forEach(r => next.delete(r.recurso_id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtrados.forEach(r => next.add(r.recurso_id));
        return next;
      });
    }
  };

  const handleAbrirSolModal = async () => {
    // Pre-llenar cantidades con "por solicitar" de cada recurso seleccionado
    const initCantidades = {};
    recursos.filter(r => selectedIds.has(r.recurso_id)).forEach(r => {
      initCantidades[r.recurso_id] = r.cantidad_a_solicitar > 0 ? r.cantidad_a_solicitar : r.cantidad_pendiente > 0 ? r.cantidad_pendiente : 1;
    });
    setCantidades(initCantidades);
    setFormSol({ proyecto: '', bodega: '', observaciones: '' });
    setShowSolModal(true);
    try {
      const [proyRes, bodRes] = await Promise.all([
        proyectosListG.length === 0 ? proyectoService.getAll() : Promise.resolve(null),
        bodegasListG.length === 0 ? bodegaService.getBodegas() : Promise.resolve(null),
      ]);
      if (proyRes) setProyectosListG(ensureArray(proyRes.data?.results || proyRes.data));
      if (bodRes) setBodegasListG(ensureArray(bodRes.data?.results || bodRes.data));
    } catch { /* ignore */ }
  };

  const handleCrearSolicitud = async () => {
    if (!formSol.proyecto) { toast.error('Debe seleccionar un Proyecto'); return; }
    const detalles = recursos
      .filter(r => selectedIds.has(r.recurso_id))
      .map(r => ({ recurso: r.recurso_id, cantidad_solicitada: Number(cantidades[r.recurso_id] || 1) }))
      .filter(d => d.cantidad_solicitada > 0);
    if (detalles.length === 0) { toast.error('Debe haber al menos un recurso con cantidad'); return; }
    try {
      setSavingSol(true);
      const data = { proyecto: formSol.proyecto, detalles_data: detalles };
      if (formSol.bodega) data.bodega = formSol.bodega;
      if (formSol.observaciones) data.observaciones_solicitud = formSol.observaciones;
      await bodegaService.createSolicitudRecurso(data);
      toast.success('Solicitud creada exitosamente');
      setShowSolModal(false);
      setSelectedIds(new Set());
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === 'object') {
        toast.error(Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | '));
      } else { toast.error('Error al crear solicitud'); }
    } finally { setSavingSol(false); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
  const recursosSeleccionados = recursos.filter(r => selectedIds.has(r.recurso_id));

  if (loading) return <Loading />;

  return (
    <div style={{ paddingBottom: selectedIds.size > 0 ? '5rem' : 0 }}>
      {/* Controles superiores */}
      <div className="filtros-container" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Horizonte:</label>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {HORIZONTE_OPTS.map(o => (
              <button key={o.value} onClick={() => setHorizonte(o.value)} style={{
                padding: '0.375rem 0.75rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', border: 'none',
                background: horizonte === o.value ? 'var(--geves-gradient-primary)' : '#f3f4f6',
                color: horizonte === o.value ? 'white' : '#374151',
              }}>{o.label}</button>
            ))}
          </div>
        </div>
        <input className="input-filtro" placeholder="Buscar recurso..." value={buscar} onChange={e => setBuscar(e.target.value)} style={{ maxWidth: 220 }} />
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {[['todos', 'Todos'], ['solicitar', '⚠ Por solicitar'], ['ok', '✓ Con stock']].map(([v, l]) => (
            <button key={v} onClick={() => setFiltro(v)} style={{
              padding: '0.375rem 0.75rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', border: 'none',
              background: filtro === v ? (v === 'solicitar' ? '#fee2e2' : v === 'ok' ? '#d1fae5' : '#f3f4f6') : '#f9fafb',
              color: filtro === v ? (v === 'solicitar' ? '#991b1b' : v === 'ok' ? '#065f46' : '#374151') : '#6b7280',
            }}>{l}</button>
          ))}
        </div>
        <button className="btn-primary" onClick={cargar} style={{ marginLeft: 'auto' }}>↻ Actualizar</button>
      </div>

      {/* Tarjetas resumen */}
      {reporte && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ background: 'white', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #6b7280' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Total recursos</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827' }}>{reporte.total_recursos_con_stock}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>con control de stock</div>
          </div>
          <div style={{ background: 'white', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Stock suficiente</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46' }}>{reporte.recursos_con_stock_suficiente}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>no requieren solicitud</div>
          </div>
          <div style={{ background: 'white', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #ef4444' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Requieren solicitud</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#991b1b' }}>{reporte.recursos_requieren_solicitud}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>con déficit de stock</div>
          </div>
          <div style={{ background: 'white', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Ventana de análisis</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e40af' }}>{reporte.fecha_inicio} → {reporte.fecha_fin}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{horizonte} días</div>
          </div>
        </div>
      )}

      {/* Lista de recursos */}
      {filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', background: 'white', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {recursos.length === 0 ? 'No hay tareas con recursos de stock en el período seleccionado' : 'No hay recursos que coincidan con el filtro'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Fila de selección general */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem', background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              ref={el => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
              onChange={toggleSelectAll}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#69B32D' }}
            />
            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>
              {selectedIds.size === 0
                ? `Seleccionar todos (${filtrados.length})`
                : `${selectedIds.size} recurso${selectedIds.size > 1 ? 's' : ''} seleccionado${selectedIds.size > 1 ? 's' : ''}`}
            </span>
            {selectedIds.size > 0 && (
              <button onClick={() => setSelectedIds(new Set())} style={{ marginLeft: 4, background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.78rem' }}>
                Limpiar selección
              </button>
            )}
          </div>

          {filtrados.map(r => {
            const exp = expandidos[r.recurso_id];
            const sel = selectedIds.has(r.recurso_id);
            const stockPct = r.cantidad_pendiente > 0 ? Math.min(100, (r.stock_disponible_total / r.cantidad_pendiente) * 100) : 100;
            const barColor = r.requiere_solicitud ? '#ef4444' : '#10b981';

            return (
              <div key={r.recurso_id} style={{
                background: 'white', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                border: sel ? '2px solid #69B32D' : (r.requiere_solicitud ? '1px solid #fca5a5' : '1px solid #6ee7b7'),
                overflow: 'hidden', transition: 'border 0.15s'
              }}>
                {/* Cabecera */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', cursor: 'pointer' }}
                  onClick={() => toggleExpandir(r.recurso_id)}>

                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={(e) => toggleSelect(e, r.recurso_id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: '#69B32D' }}
                  />

                  {/* Urgencia */}
                  <UrgenciaBadge dias={r.dias_hasta_primera_tarea} />

                  {/* Nombre */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{r.recurso_nombre}</div>
                    {r.recurso_descripcion && <div style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.recurso_descripcion}</div>}
                  </div>

                  {/* Métricas */}
                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8rem', flexShrink: 0 }}>
                    {[
                      ['Requerido', fmt(r.cantidad_requerida), '#374151'],
                      ['Stock', fmt(r.stock_disponible_total), r.requiere_solicitud ? '#dc2626' : '#059669'],
                      ['Ya solicitado', fmt(r.cantidad_solicitada), '#6b7280'],
                    ].map(([label, val, color]) => (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <div style={{ color: '#9ca3af', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
                        <div style={{ fontWeight: 700, color }}>{val}</div>
                      </div>
                    ))}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase' }}>Por solicitar</div>
                      <div style={{ fontWeight: 700, color: r.cantidad_a_solicitar > 0 ? '#dc2626' : '#059669' }}>
                        {r.cantidad_a_solicitar > 0 ? `⚠ ${fmt(r.cantidad_a_solicitar)}` : '✓ 0'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#9ca3af', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase' }}>Tareas</div>
                      <div style={{ fontWeight: 700, color: '#374151' }}>{r.tareas_asociadas?.length || 0}</div>
                    </div>
                  </div>

                  {/* Barra cobertura */}
                  <div style={{ width: 80, flexShrink: 0 }}>
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af', fontWeight: 600, textAlign: 'center', marginBottom: 2 }}>{Math.round(stockPct)}% cubierto</div>
                    <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${stockPct}%`, background: barColor, borderRadius: 3 }} />
                    </div>
                  </div>

                  <span style={{ color: '#9ca3af', fontSize: '1rem', flexShrink: 0 }}>{exp ? '▾' : '▸'}</span>
                </div>

                {/* Detalle expandible */}
                {exp && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '0.75rem 1rem', background: '#fafafa', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {r.stock_por_bodega?.length > 0 && (
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stock por bodega</div>
                        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            <th style={{ textAlign: 'left', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Bodega</th>
                            <th style={{ textAlign: 'right', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Disponible</th>
                            <th style={{ textAlign: 'right', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Stock mín.</th>
                          </tr></thead>
                          <tbody>
                            {r.stock_por_bodega.map(b => (
                              <tr key={b.bodega_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '4px 0', color: '#374151' }}>{b.bodega_nombre}</td>
                                <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600, color: b.cantidad_disponible > 0 ? '#059669' : '#dc2626' }}>{fmt(b.cantidad_disponible)}</td>
                                <td style={{ padding: '4px 0', textAlign: 'right', color: '#9ca3af' }}>{fmt(b.stock_minimo)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {r.tareas_asociadas?.length > 0 && (
                      <div style={{ flex: 2, minWidth: 280 }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tareas que requieren este recurso</div>
                        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            <th style={{ textAlign: 'left', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Tarea</th>
                            <th style={{ textAlign: 'left', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Evento</th>
                            <th style={{ textAlign: 'right', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Inicio</th>
                            <th style={{ textAlign: 'right', color: '#9ca3af', fontWeight: 600, paddingBottom: 4 }}>Cantidad</th>
                          </tr></thead>
                          <tbody>
                            {r.tareas_asociadas.map(t => (
                              <tr key={t.tarea_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '4px 4px 4px 0', color: '#374151', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.tarea_titulo}</td>
                                <td style={{ padding: '4px 4px 4px 0', color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.evento_titulo}</td>
                                <td style={{ padding: '4px 0', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap' }}>{t.fecha_inicio}</td>
                                <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600, color: '#374151' }}>{fmt(t.cantidad_presupuesto)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Barra flotante de acción ── */}
      {selectedIds.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1f2937', color: 'white', borderRadius: 12,
          padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)', zIndex: 200, whiteSpace: 'nowrap'
        }}>
          <span style={{ fontSize: '0.875rem' }}>
            <strong style={{ color: '#69B32D' }}>{selectedIds.size}</strong> recurso{selectedIds.size > 1 ? 's' : ''} seleccionado{selectedIds.size > 1 ? 's' : ''}
          </span>
          <button onClick={() => setSelectedIds(new Set())} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', color: '#d1d5db',
            borderRadius: 6, padding: '0.375rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem'
          }}>Cancelar</button>
          <button onClick={handleAbrirSolModal} style={{
            background: 'linear-gradient(135deg, #69B32D, #5a9e26)', border: 'none', color: 'white',
            borderRadius: 6, padding: '0.5rem 1.25rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 700
          }}>+ Crear Solicitud</button>
        </div>
      )}

      {/* ── Modal crear solicitud ── */}
      {showSolModal && (
        <div className="pago-modal-overlay" onClick={() => setShowSolModal(false)}>
          <div className="pago-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="pago-modal-header">
              <h3>Nueva Solicitud de Recursos</h3>
              <button className="btn-icon" onClick={() => setShowSolModal(false)}>&times;</button>
            </div>
            <div className="pago-modal-body">
              {/* Campos generales */}
              <div className="pago-form-grid" style={{ marginBottom: '1.25rem' }}>
                <div className="pago-form-group">
                  <label>Proyecto *</label>
                  <select value={formSol.proyecto} onChange={e => setFormSol(p => ({ ...p, proyecto: e.target.value }))}>
                    <option value="">Seleccionar proyecto...</option>
                    {proyectosListG.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Bodega destino</label>
                  <select value={formSol.bodega} onChange={e => setFormSol(p => ({ ...p, bodega: e.target.value }))}>
                    <option value="">Sin bodega específica</option>
                    {bodegasListG.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group pago-form-full">
                  <label>Observaciones</label>
                  <textarea value={formSol.observaciones} onChange={e => setFormSol(p => ({ ...p, observaciones: e.target.value }))} rows={2} placeholder="Observaciones opcionales..." />
                </div>
              </div>

              {/* Tabla de recursos seleccionados */}
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                Recursos a solicitar ({recursosSeleccionados.length})
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Recurso</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Stock actual</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Déficit</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 700 }}>Cantidad a solicitar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recursosSeleccionados.map(r => (
                      <tr key={r.recurso_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#111827', fontWeight: 600 }}>{r.recurso_nombre}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: r.stock_disponible_total > 0 ? '#059669' : '#dc2626' }}>{fmt(r.stock_disponible_total)}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: r.cantidad_a_solicitar > 0 ? '#dc2626' : '#059669' }}>{fmt(r.cantidad_a_solicitar)}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={cantidades[r.recurso_id] ?? ''}
                            onChange={e => setCantidades(prev => ({ ...prev, [r.recurso_id]: e.target.value }))}
                            style={{
                              width: 90, textAlign: 'right', padding: '0.25rem 0.5rem',
                              border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem',
                              fontWeight: 700, color: '#111827'
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="pago-modal-footer">
              <button className="btn-secondary" onClick={() => setShowSolModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleCrearSolicitud} disabled={savingSol}>
                {savingSol ? 'Creando...' : `Crear Solicitud (${recursosSeleccionados.length} recursos)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// ORDENES DE COMPRA TAB
// ============================================================
const OrdenesCompraTab = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroNumero, setFiltroNumero] = useState('');
  const [showCrearOC, setShowCrearOC] = useState(false);
  const [formOC, setFormOC] = useState({ numero_oc: '', proveedor: '', proyecto: '', fecha_emision: new Date().toISOString().split('T')[0], fecha_vencimiento: '', descripcion: '' });
  const [savingOC, setSavingOC] = useState(false);
  const [proyectosListOC, setProyectosListOC] = useState([]);
  const [proveedoresListOC, setProveedoresListOC] = useState([]);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await ordenCompraService.getAll();
      setOrdenes(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar ordenes de compra'); }
    finally { setLoading(false); }
  };

  const handleCambiarEstado = async (id, estado) => {
    try {
      await ordenCompraService.cambiarEstado(id, estado);
      toast.success(`Orden cambiada a ${estado}`);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || err.response?.data?.error || 'Error al cambiar estado'); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta orden de compra?')) return;
    try {
      await ordenCompraService.delete(id);
      toast.success('Orden eliminada');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAbrirCrearOC = async () => {
    setFormOC({ numero_oc: '', proveedor: '', proyecto: '', fecha_emision: new Date().toISOString().split('T')[0], fecha_vencimiento: '', descripcion: '' });
    setShowCrearOC(true);
    try {
      const [provRes, proyRes] = await Promise.all([
        proveedoresListOC.length === 0 ? proveedorService.getAll() : Promise.resolve({ data: { results: proveedoresListOC } }),
        proyectosListOC.length === 0 ? proyectoService.getAll() : Promise.resolve({ data: { results: proyectosListOC } }),
      ]);
      if (proveedoresListOC.length === 0) setProveedoresListOC(ensureArray(provRes.data.results || provRes.data));
      if (proyectosListOC.length === 0) setProyectosListOC(ensureArray(proyRes.data.results || proyRes.data));
    } catch { /* ignore */ }
  };

  const handleGuardarOC = async () => {
    try {
      setSavingOC(true);
      const data = { fecha_emision: formOC.fecha_emision };
      if (formOC.numero_oc) data.numero_oc = formOC.numero_oc;
      if (formOC.proveedor) data.proveedor = formOC.proveedor;
      if (formOC.proyecto) data.proyecto = formOC.proyecto;
      if (formOC.fecha_vencimiento) data.fecha_vencimiento = formOC.fecha_vencimiento;
      if (formOC.descripcion) data.descripcion = formOC.descripcion;
      await ordenCompraService.create(data);
      toast.success('Orden de compra creada exitosamente');
      setShowCrearOC(false);
      cargar();
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        toast.error(msgs);
      } else { toast.error('Error al crear orden de compra'); }
    } finally { setSavingOC(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CL') : '-';

  const getEstadoClass = (estado) => {
    const map = { pendiente: 'badge-borrador', emitida: 'badge-emitida', recibida: 'badge-pagada', facturada: 'badge-aprobado', cancelada: 'badge-anulada' };
    return map[estado] || 'badge-default';
  };

  const filtradas = ordenes.filter(o => {
    if (filtroEstado && o.estado !== filtroEstado) return false;
    if (filtroNumero && !(o.numero_oc || '').toLowerCase().includes(filtroNumero.toLowerCase())) return false;
    return true;
  });

  const ocPorEstado = {};
  OC_COLUMNS.forEach(col => { ocPorEstado[col.id] = []; });
  filtradas.forEach(o => {
    if (ocPorEstado[o.estado]) ocPorEstado[o.estado].push(o);
    else ocPorEstado['cancelada'].push(o);
  });

  if (loading) return <Loading />;

  return (
    <>
      <div className="filtros-container">
        <input className="input-filtro" placeholder="Buscar por N° OC..." value={filtroNumero} onChange={e => setFiltroNumero(e.target.value)} />
        <select className="select-filtro" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="emitida">Emitida</option>
          <option value="recibida">Recibida</option>
          <option value="facturada">Facturada</option>
          <option value="cancelada">Cancelada</option>
        </select>
        <button className="btn-primary" onClick={handleAbrirCrearOC}><FiPlus /> Nueva Orden de Compra</button>
      </div>
      <div className="docleg-kanban-board">
        {OC_COLUMNS.map(col => (
          <div key={col.id} className="docleg-kanban-column">
            <div className="docleg-kanban-col-header" style={{ borderTopColor: col.color, background: col.bgColor }}>
              <span className="docleg-kanban-col-icon">{col.icon}</span>
              <span className="docleg-kanban-col-title" style={{ color: col.color }}>{col.label}</span>
              <span className="docleg-kanban-col-count" style={{ background: col.color }}>{ocPorEstado[col.id].length}</span>
            </div>
            <div className="docleg-kanban-col-body">
              {ocPorEstado[col.id].length === 0 ? (
                <div className="docleg-kanban-empty">Sin ordenes</div>
              ) : ocPorEstado[col.id].map(o => (
                <div key={o.id} className="docleg-card">
                  <div className="docleg-card-header">
                    <span className="docleg-card-id">OC #{o.numero_oc || o.id}</span>
                    <span className={`badge ${getEstadoClass(o.estado)}`}>{o.estado}</span>
                  </div>
                  {o.proveedor_nombre && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Proveedor</span>
                      <span className="docleg-card-value">{o.proveedor_nombre}</span>
                    </div>
                  )}
                  {o.proyecto_nombre && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Proyecto</span>
                      <span className="docleg-card-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>{o.proyecto_nombre}</span>
                    </div>
                  )}
                  <div className="docleg-card-row">
                    <span className="docleg-card-label">Emisión</span>
                    <span className="docleg-card-value">{formatDate(o.fecha_emision)}</span>
                  </div>
                  {o.fecha_vencimiento && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Vencimiento</span>
                      <span className="docleg-card-value">{formatDate(o.fecha_vencimiento)}</span>
                    </div>
                  )}
                  <div className="docleg-card-row">
                    <span className="docleg-card-label">Items</span>
                    <span className="docleg-card-value">{o.numero_items || 0}</span>
                  </div>
                  <div className="docleg-card-total" style={{ color: '#059669' }}>
                    {formatCurrency(o.total_oc)}
                  </div>
                  <div className="docleg-card-actions">
                    {o.estado === 'pendiente' && (
                      <button className="docleg-card-action-btn" style={{ background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' }} onClick={() => handleCambiarEstado(o.id, 'emitida')}>
                        📤 Emitir
                      </button>
                    )}
                    {o.estado === 'emitida' && (
                      <button className="docleg-card-action-btn" style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }} onClick={() => handleCambiarEstado(o.id, 'recibida')}>
                        📦 Recibir
                      </button>
                    )}
                    {o.estado === 'pendiente' && (
                      <button className="docleg-card-action-btn docleg-card-action-del" onClick={() => handleEliminar(o.id)}>
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showCrearOC && (
        <div className="pago-modal-overlay" onClick={() => setShowCrearOC(false)}>
          <div className="pago-modal" onClick={e => e.stopPropagation()}>
            <div className="pago-modal-header">
              <h3>Nueva Orden de Compra</h3>
              <button className="btn-icon" onClick={() => setShowCrearOC(false)}>&times;</button>
            </div>
            <div className="pago-modal-body">
              <div className="pago-form-grid">
                <div className="pago-form-group">
                  <label>N° OC</label>
                  <input value={formOC.numero_oc} onChange={e => setFormOC({ ...formOC, numero_oc: e.target.value })} placeholder="Ej: OC-001" />
                </div>
                <div className="pago-form-group">
                  <label>Proveedor</label>
                  <select value={formOC.proveedor} onChange={e => setFormOC({ ...formOC, proveedor: e.target.value })}>
                    <option value="">Seleccionar proveedor...</option>
                    {proveedoresListOC.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Proyecto</label>
                  <select value={formOC.proyecto} onChange={e => setFormOC({ ...formOC, proyecto: e.target.value })}>
                    <option value="">Seleccionar proyecto...</option>
                    {proyectosListOC.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Fecha Emision *</label>
                  <input type="date" value={formOC.fecha_emision} onChange={e => setFormOC({ ...formOC, fecha_emision: e.target.value })} />
                </div>
                <div className="pago-form-group">
                  <label>Fecha Vencimiento</label>
                  <input type="date" value={formOC.fecha_vencimiento} onChange={e => setFormOC({ ...formOC, fecha_vencimiento: e.target.value })} />
                </div>
                <div className="pago-form-group pago-form-full">
                  <label>Descripcion</label>
                  <textarea value={formOC.descripcion} onChange={e => setFormOC({ ...formOC, descripcion: e.target.value })} rows={3} placeholder="Descripcion de la orden de compra..." />
                </div>
              </div>
            </div>
            <div className="pago-modal-footer">
              <button className="btn-secondary" onClick={() => setShowCrearOC(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleGuardarOC} disabled={savingOC}>
                {savingOC ? 'Creando...' : 'Crear Orden de Compra'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================
// GUIAS DE DESPACHO TAB
// ============================================================
const GuiasDespachoTab = () => {
  const [guias, setGuias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroNumero, setFiltroNumero] = useState('');
  const [showCrearGuia, setShowCrearGuia] = useState(false);
  const [formGuia, setFormGuia] = useState({ numero_guia: '', orden_compra: '', proveedor: '', proyecto: '', bodega: '', fecha_emision_guia: new Date().toISOString().split('T')[0], fecha_recepcion: '' });
  const [savingGuia, setSavingGuia] = useState(false);
  const [proyectosListGuia, setProyectosListGuia] = useState([]);
  const [proveedoresListGuia, setProveedoresListGuia] = useState([]);
  const [bodegasList, setBodegasList] = useState([]);
  const [ordenesCompraList, setOrdenesCompraList] = useState([]);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await bodegaService.getGuiasDespacho();
      setGuias(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar guias de despacho'); }
    finally { setLoading(false); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta guia de despacho?')) return;
    try {
      await bodegaService.deleteGuiaDespacho(id);
      toast.success('Guia eliminada');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAbrirCrearGuia = async () => {
    setFormGuia({ numero_guia: '', orden_compra: '', proveedor: '', proyecto: '', bodega: '', fecha_emision_guia: new Date().toISOString().split('T')[0], fecha_recepcion: '' });
    setShowCrearGuia(true);
    try {
      const [provRes, proyRes, bodRes, ocRes] = await Promise.all([
        proveedoresListGuia.length === 0 ? proveedorService.getAll() : Promise.resolve(null),
        proyectosListGuia.length === 0 ? proyectoService.getAll() : Promise.resolve(null),
        bodegasList.length === 0 ? bodegaService.getBodegas() : Promise.resolve(null),
        ordenesCompraList.length === 0 ? ordenCompraService.getAll() : Promise.resolve(null),
      ]);
      if (provRes) setProveedoresListGuia(ensureArray(provRes.data.results || provRes.data));
      if (proyRes) setProyectosListGuia(ensureArray(proyRes.data.results || proyRes.data));
      if (bodRes) setBodegasList(ensureArray(bodRes.data.results || bodRes.data));
      if (ocRes) setOrdenesCompraList(ensureArray(ocRes.data.results || ocRes.data));
    } catch { /* ignore */ }
  };

  const handleGuardarGuia = async () => {
    try {
      setSavingGuia(true);
      const data = { fecha_emision_guia: formGuia.fecha_emision_guia };
      if (formGuia.numero_guia) data.numero_guia = formGuia.numero_guia;
      if (formGuia.orden_compra) data.orden_compra = formGuia.orden_compra;
      if (formGuia.proveedor) data.proveedor = formGuia.proveedor;
      if (formGuia.proyecto) data.proyecto = formGuia.proyecto;
      if (formGuia.bodega) data.bodega = formGuia.bodega;
      if (formGuia.fecha_recepcion) data.fecha_recepcion = formGuia.fecha_recepcion;
      await bodegaService.createGuiaDespacho(data);
      toast.success('Guia de despacho creada exitosamente');
      setShowCrearGuia(false);
      cargar();
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        toast.error(msgs);
      } else { toast.error('Error al crear guia de despacho'); }
    } finally { setSavingGuia(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CL') : '-';

  const getEstadoClass = (estado) => {
    const map = { borrador: 'badge-borrador', recepcionado: 'badge-pagada', anulado: 'badge-anulada' };
    return map[estado] || 'badge-default';
  };

  const filtradas = guias.filter(g => {
    if (filtroEstado && g.estado !== filtroEstado) return false;
    if (filtroNumero && !(g.numero_guia || '').toLowerCase().includes(filtroNumero.toLowerCase())) return false;
    return true;
  });

  const guiasPorEstado = {};
  GUIA_COLUMNS.forEach(col => { guiasPorEstado[col.id] = []; });
  filtradas.forEach(g => {
    if (guiasPorEstado[g.estado]) guiasPorEstado[g.estado].push(g);
    else guiasPorEstado['anulado'].push(g);
  });

  if (loading) return <Loading />;

  return (
    <>
      <div className="filtros-container">
        <input className="input-filtro" placeholder="Buscar por N° guia..." value={filtroNumero} onChange={e => setFiltroNumero(e.target.value)} />
        <select className="select-filtro" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="recepcionado">Recepcionado</option>
          <option value="anulado">Anulado</option>
        </select>
        <button className="btn-primary" onClick={handleAbrirCrearGuia}><FiPlus /> Nueva Guia de Despacho</button>
      </div>
      <div className="docleg-kanban-board">
        {GUIA_COLUMNS.map(col => (
          <div key={col.id} className="docleg-kanban-column">
            <div className="docleg-kanban-col-header" style={{ borderTopColor: col.color, background: col.bgColor }}>
              <span className="docleg-kanban-col-icon">{col.icon}</span>
              <span className="docleg-kanban-col-title" style={{ color: col.color }}>{col.label}</span>
              <span className="docleg-kanban-col-count" style={{ background: col.color }}>{guiasPorEstado[col.id].length}</span>
            </div>
            <div className="docleg-kanban-col-body">
              {guiasPorEstado[col.id].length === 0 ? (
                <div className="docleg-kanban-empty">Sin guias</div>
              ) : guiasPorEstado[col.id].map(g => (
                <div key={g.id} className="docleg-card">
                  <div className="docleg-card-header">
                    <span className="docleg-card-id">GD #{g.numero_guia || g.id}</span>
                    <span className={`badge ${getEstadoClass(g.estado)}`}>{g.estado_display || g.estado}</span>
                  </div>
                  {g.orden_compra_numero && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">OC</span>
                      <span className="docleg-card-badge" style={{ background: '#ede9fe', color: '#5b21b6' }}>{g.orden_compra_numero}</span>
                    </div>
                  )}
                  {g.proveedor_nombre && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Proveedor</span>
                      <span className="docleg-card-value">{g.proveedor_nombre}</span>
                    </div>
                  )}
                  {g.proyecto_nombre && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Proyecto</span>
                      <span className="docleg-card-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>{g.proyecto_nombre}</span>
                    </div>
                  )}
                  {g.bodega_nombre && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Bodega</span>
                      <span className="docleg-card-value">{g.bodega_nombre}</span>
                    </div>
                  )}
                  <div className="docleg-card-row">
                    <span className="docleg-card-label">Emisión</span>
                    <span className="docleg-card-value">{formatDate(g.fecha_emision_guia)}</span>
                  </div>
                  {g.fecha_recepcion && (
                    <div className="docleg-card-row">
                      <span className="docleg-card-label">Recepción</span>
                      <span className="docleg-card-value">{formatDate(g.fecha_recepcion)}</span>
                    </div>
                  )}
                  {g.estado === 'borrador' && (
                    <div className="docleg-card-actions">
                      <button className="docleg-card-action-btn docleg-card-action-del" onClick={() => handleEliminar(g.id)}>
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showCrearGuia && (
        <div className="pago-modal-overlay" onClick={() => setShowCrearGuia(false)}>
          <div className="pago-modal" onClick={e => e.stopPropagation()}>
            <div className="pago-modal-header">
              <h3>Nueva Guia de Despacho</h3>
              <button className="btn-icon" onClick={() => setShowCrearGuia(false)}>&times;</button>
            </div>
            <div className="pago-modal-body">
              <div className="pago-form-grid">
                <div className="pago-form-group">
                  <label>N° Guia</label>
                  <input value={formGuia.numero_guia} onChange={e => setFormGuia({ ...formGuia, numero_guia: e.target.value })} placeholder="Ej: GD-001" />
                </div>
                <div className="pago-form-group">
                  <label>OC Asociada</label>
                  <select value={formGuia.orden_compra} onChange={e => setFormGuia({ ...formGuia, orden_compra: e.target.value })}>
                    <option value="">Sin OC asociada</option>
                    {ordenesCompraList.map(o => <option key={o.id} value={o.id}>{o.numero_oc || `OC #${o.id}`}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Proveedor</label>
                  <select value={formGuia.proveedor} onChange={e => setFormGuia({ ...formGuia, proveedor: e.target.value })}>
                    <option value="">Seleccionar proveedor...</option>
                    {proveedoresListGuia.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Proyecto</label>
                  <select value={formGuia.proyecto} onChange={e => setFormGuia({ ...formGuia, proyecto: e.target.value })}>
                    <option value="">Seleccionar proyecto...</option>
                    {proyectosListGuia.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Bodega</label>
                  <select value={formGuia.bodega} onChange={e => setFormGuia({ ...formGuia, bodega: e.target.value })}>
                    <option value="">Seleccionar bodega...</option>
                    {bodegasList.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Fecha Emision *</label>
                  <input type="date" value={formGuia.fecha_emision_guia} onChange={e => setFormGuia({ ...formGuia, fecha_emision_guia: e.target.value })} />
                </div>
                <div className="pago-form-group">
                  <label>Fecha Recepcion</label>
                  <input type="date" value={formGuia.fecha_recepcion} onChange={e => setFormGuia({ ...formGuia, fecha_recepcion: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="pago-modal-footer">
              <button className="btn-secondary" onClick={() => setShowCrearGuia(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleGuardarGuia} disabled={savingGuia}>
                {savingGuia ? 'Creando...' : 'Crear Guia de Despacho'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================
// CRONOGRAMA TAB
// ============================================================
const PERIODOS_CRONO = [
  { label: 'Vencido',      key: 'vencido',      color: '#dc2626', icon: '⚠️' },
  { label: 'Esta Semana',  key: 'esta_semana',  color: '#f59e0b', icon: '📅' },
  { label: 'Este Mes',     key: 'este_mes',     color: '#3b82f6', icon: '🗓️' },
  { label: 'Proximo Mes',  key: 'proximo_mes',  color: '#059669', icon: '📆' },
];

const CronogramaTab = () => {
  const [datosResumen, setDatosResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      // Rango amplio: desde 1 año atrás para capturar vencidos, hasta 2 meses adelante
      const hoy = new Date();
      const fechaDesde = new Date(hoy.getFullYear() - 1, hoy.getMonth(), hoy.getDate()).toISOString().split('T')[0];
      const finProxMes = new Date(hoy.getFullYear(), hoy.getMonth() + 2, 0);
      const fechaHasta = finProxMes.toISOString().split('T')[0];
      const res = await documentosLegalesService.getFlujoCaja(fechaDesde, fechaHasta, null, null, 'lista');
      setDatosResumen(res.data);
    } catch { toast.error('Error al cargar cronograma'); }
    finally { setLoading(false); }
  };

  const agruparPorPeriodo = (resumen) => {
    if (!resumen) return { vencido: [], esta_semana: [], este_mes: [], proximo_mes: [] };
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const enSieteDias = new Date(hoy); enSieteDias.setDate(hoy.getDate() + 7);
    const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    const finProxMes = new Date(hoy.getFullYear(), hoy.getMonth() + 2, 0);
    const grupos = { vencido: [], esta_semana: [], este_mes: [], proximo_mes: [] };
    const todosLosDocs = resumen.datos?.flatMap(dia => dia.documentos) || [];
    todosLosDocs.forEach(doc => {
      const fechaRef = doc.fecha_pago_programada || doc.fecha_vencimiento;
      if (!fechaRef) return;
      const fv = new Date(fechaRef + 'T00:00:00');
      if (isNaN(fv)) return;
      if (fv < hoy) grupos.vencido.push(doc);
      else if (fv <= enSieteDias) grupos.esta_semana.push(doc);
      else if (fv <= finMes) grupos.este_mes.push(doc);
      else if (fv <= finProxMes) grupos.proximo_mes.push(doc);
    });
    return grupos;
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);
  const formatDate = (d) => { if (!d) return '-'; const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('es-CL'); };
  const sumar = (arr) => arr.reduce((s, f) => s + parseFloat(f.monto_total || 0), 0);

  if (loading) return <Loading />;

  const grupos = agruparPorPeriodo(datosResumen);
  const resumen = datosResumen?.resumen || {};

  const totalPagar = PERIODOS_CRONO.reduce((sum, p) => sum + sumar(grupos[p.key].filter(d => d.es_por_pagar)), 0);
  const totalCobrar = PERIODOS_CRONO.reduce((sum, p) => sum + sumar(grupos[p.key].filter(d => !d.es_por_pagar)), 0);
  const flujoNeto = totalCobrar - totalPagar;

  return (
    <div className="crono-container">
      {/* KPI Cards */}
      <div className="crono-kpis">
        <div className="crono-kpi crono-kpi-pagar">
          <div className="crono-kpi-label">Total por Pagar</div>
          <div className="crono-kpi-value">{formatCurrency(totalPagar)}</div>
        </div>
        <div className="crono-kpi crono-kpi-cobrar">
          <div className="crono-kpi-label">Total por Cobrar</div>
          <div className="crono-kpi-value">{formatCurrency(totalCobrar)}</div>
        </div>
        <div className={`crono-kpi ${flujoNeto >= 0 ? 'crono-kpi-positivo' : 'crono-kpi-negativo'}`}>
          <div className="crono-kpi-label">Flujo Neto</div>
          <div className="crono-kpi-value">{formatCurrency(flujoNeto)}</div>
        </div>
        {resumen.documentos_vencidos > 0 && (
          <div className="crono-kpi crono-kpi-vencidos">
            <div className="crono-kpi-label">Docs Vencidos</div>
            <div className="crono-kpi-value">{resumen.documentos_vencidos}</div>
            <div className="crono-kpi-sub">{formatCurrency(resumen.total_vencido)}</div>
          </div>
        )}
      </div>

      {/* Periodos */}
      {PERIODOS_CRONO.map(periodo => {
        const todosDelPeriodo = grupos[periodo.key] || [];
        const porPagar = todosDelPeriodo.filter(d => d.es_por_pagar);
        const porCobrar = todosDelPeriodo.filter(d => !d.es_por_pagar);
        const totalP = sumar(porPagar);
        const totalC = sumar(porCobrar);
        const neto = totalC - totalP;
        const tieneItems = todosDelPeriodo.length > 0;

        return (
          <div key={periodo.key} className="crono-section" style={{ borderLeftColor: periodo.color }}>
            <div className="crono-section-header">
              <h3 style={{ color: periodo.color }}>
                <FiCalendar /> {periodo.label}
                <span className="crono-section-count">{todosDelPeriodo.length} docs</span>
              </h3>
              <div className="crono-section-totals">
                <span className="crono-total-pagar">Pagar: <strong>{formatCurrency(totalP)}</strong></span>
                <span className="crono-total-cobrar">Cobrar: <strong>{formatCurrency(totalC)}</strong></span>
                <span className={`crono-neto ${neto >= 0 ? 'pos' : 'neg'}`}>Neto: <strong>{formatCurrency(neto)}</strong></span>
              </div>
            </div>

            {!tieneItems ? (
              <p className="crono-empty">Sin documentos en este periodo</p>
            ) : (
              <div className="crono-columns">
                {/* Columna Por Pagar */}
                <div className="crono-col">
                  <div className="crono-col-header pagar">
                    <span>Por Pagar</span>
                    <span>{porPagar.length} docs</span>
                  </div>
                  {porPagar.length > 0 ? (
                    <>
                      <table className="docleg-data-table">
                        <thead>
                          <tr>
                            <th>Documento</th>
                            <th>Tipo</th>
                            <th>Tercero</th>
                            <th>Vencimiento</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'right' }}>Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {porPagar.map((doc, i) => (
                            <tr key={i}>
                              <td><strong>{doc.numero_documento || '-'}</strong></td>
                              <td><span className="crono-tipo-badge">{doc.tipo_documento_label || '-'}</span></td>
                              <td>{doc.tercero || '-'}</td>
                              <td>{formatDate(doc.fecha_pago_programada || doc.fecha_vencimiento)}</td>
                              <td><span className={`badge badge-${doc.estado}`}>{doc.estado_label || doc.estado}</span></td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626', fontFamily: "'Courier New', monospace" }}>
                                {formatCurrency(doc.monto_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="crono-subtotal pagar-sub">
                        Subtotal: <strong>{formatCurrency(totalP)}</strong>
                      </div>
                    </>
                  ) : (
                    <p className="crono-empty-col">Sin documentos por pagar</p>
                  )}
                </div>

                {/* Columna Por Cobrar */}
                <div className="crono-col">
                  <div className="crono-col-header cobrar">
                    <span>Por Cobrar</span>
                    <span>{porCobrar.length} docs</span>
                  </div>
                  {porCobrar.length > 0 ? (
                    <>
                      <table className="docleg-data-table">
                        <thead>
                          <tr>
                            <th>Documento</th>
                            <th>Tipo</th>
                            <th>Tercero</th>
                            <th>Vencimiento</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'right' }}>Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {porCobrar.map((doc, i) => (
                            <tr key={i}>
                              <td><strong>{doc.numero_documento || '-'}</strong></td>
                              <td><span className="crono-tipo-badge">{doc.tipo_documento_label || '-'}</span></td>
                              <td>{doc.tercero || '-'}</td>
                              <td>{formatDate(doc.fecha_pago_programada || doc.fecha_vencimiento)}</td>
                              <td><span className={`badge badge-${doc.estado}`}>{doc.estado_label || doc.estado}</span></td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669', fontFamily: "'Courier New', monospace" }}>
                                {formatCurrency(doc.monto_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="crono-subtotal cobrar-sub">
                        Subtotal: <strong>{formatCurrency(totalC)}</strong>
                      </div>
                    </>
                  ) : (
                    <p className="crono-empty-col">Sin documentos por cobrar</p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// DOCUMENTOS LEGALES TAB (Kanban original)
// ============================================================
const DocumentosLegalesTab = ({ onNuevoPago }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [filtroNumero, setFiltroNumero] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [documentoEdit, setDocumentoEdit] = useState(null);
  const [selectedDocumentoId, setSelectedDocumentoId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => { cargarDocumentos(); }, []);

  const cargarDocumentos = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiBaseUrl();
      const response = await api.get(`${API_BASE_URL}/documentos-legales/`);
      setDocumentos(response.data.results || response.data);
    } catch {
      toast.error('Error al cargar los documentos legales');
    } finally {
      setLoading(false);
    }
  };

  const documentosFiltrados = useMemo(() => {
    return documentos.filter(doc => {
      const cumpleTipo = !filtroTipo || doc.tipo_documento === filtroTipo;
      const cumpleProyecto = !filtroProyecto ||
        (doc.proyecto_nombre && doc.proyecto_nombre.toLowerCase().includes(filtroProyecto.toLowerCase()));
      const cumpleNumero = !filtroNumero ||
        (doc.numero_documento && doc.numero_documento.toLowerCase().includes(filtroNumero.toLowerCase()));
      return cumpleTipo && cumpleProyecto && cumpleNumero;
    });
  }, [documentos, filtroTipo, filtroProyecto, filtroNumero]);

  const documentosPorEstado = useMemo(() => {
    const grouped = {};
    COLUMNS.forEach(col => { grouped[col.id] = []; });
    documentosFiltrados.forEach(doc => {
      if (grouped[doc.estado]) grouped[doc.estado].push(doc);
    });
    return grouped;
  }, [documentosFiltrados]);

  const tiposUnicos = [...new Set(documentos.map(doc => doc.tipo_documento))];

  const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  const handleCrearDocumento = () => { setDocumentoEdit(null); setShowModal(true); };

  const handleEditarDocumento = async (documento) => {
    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await api.get(`${API_BASE_URL}/documentos-legales/${documento.id}/`);
      setDocumentoEdit(response.data);
      setShowModal(true);
    } catch { toast.error('Error al cargar el documento para editar'); }
  };

  const handleEliminarDocumento = async (documentoId) => {
    if (!window.confirm('¿Esta seguro de eliminar este documento?')) return;
    try {
      const API_BASE_URL = getApiBaseUrl();
      await api.delete(`${API_BASE_URL}/documentos-legales/${documentoId}/`);
      toast.success('Documento eliminado exitosamente');
      cargarDocumentos();
    } catch { toast.error('Error al eliminar el documento'); }
  };

  const handleCambiarEstado = async (docId, nuevoEstado) => {
    const doc = documentos.find(d => d.id === docId);
    if (!doc) return;
    const allowed = ALLOWED_TRANSITIONS[doc.estado] || [];
    if (!allowed.includes(nuevoEstado)) {
      toast.warning(`No se puede mover de "${doc.estado}" a "${nuevoEstado}"`);
      return;
    }
    setDocumentos(prev => prev.map(d => d.id === docId ? { ...d, estado: nuevoEstado } : d));
    try {
      const API_BASE_URL = getApiBaseUrl();
      await api.post(`${API_BASE_URL}/documentos-legales/${docId}/cambiar-estado/`, { nuevo_estado: nuevoEstado });
      toast.success(`Documento movido a "${nuevoEstado}"`);
    } catch {
      toast.error('Error al cambiar el estado del documento');
      cargarDocumentos();
    }
  };

  const activeDoc = activeId ? documentos.find(d => d.id === activeId) : null;
  const activeDocEstado = activeDoc?.estado || null;
  const allowedTargets = activeDocEstado ? (ALLOWED_TRANSITIONS[activeDocEstado] || []) : [];

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const docId = active.id;
    let targetColumn = over.id;
    const isColumn = COLUMNS.some(c => c.id === targetColumn);
    if (!isColumn) {
      const overDoc = documentos.find(d => d.id === targetColumn);
      if (overDoc) targetColumn = overDoc.estado;
      else return;
    }
    const doc = documentos.find(d => d.id === docId);
    if (!doc || doc.estado === targetColumn) return;
    handleCambiarEstado(docId, targetColumn);
  };

  if (loading) return <Loading />;

  return (
    <>
      <div className="filtros-container">
        <input type="text" placeholder="Buscar por numero..." value={filtroNumero} onChange={(e) => setFiltroNumero(e.target.value)} className="input-filtro" />
        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="select-filtro">
          <option value="">Todos los tipos</option>
          {tiposUnicos.map(tipo => (
            <option key={tipo} value={tipo}>{documentos.find(d => d.tipo_documento === tipo)?.tipo_documento_display || tipo}</option>
          ))}
        </select>
        <input type="text" placeholder="Filtrar por proyecto..." value={filtroProyecto} onChange={(e) => setFiltroProyecto(e.target.value)} className="input-filtro" />
        <button className="btn-primary" onClick={handleCrearDocumento}><FiPlus /> Nuevo Documento</button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="docleg-kanban-board">
          {COLUMNS.map(column => (
            <DocumentoLegalKanbanColumn
              key={column.id}
              column={column}
              documentos={documentosPorEstado[column.id] || []}
              onCardClick={(docId) => setSelectedDocumentoId(docId)}
              onEditDoc={handleEditarDocumento}
              onDeleteDoc={handleEliminarDocumento}
              onChangeEstado={handleCambiarEstado}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              activeId={activeId}
              activeDocEstado={activeDocEstado}
              allowedTargets={allowedTargets}
            />
          ))}
        </div>
        <DragOverlay>
          {activeDoc ? (
            <DocumentoLegalCard documento={activeDoc} onCardClick={() => {}} onEdit={() => {}} onDelete={() => {}} onChangeEstado={() => {}} formatCurrency={formatCurrency} formatDate={formatDate} overlay={true} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {selectedDocumentoId && (
        <DocumentoLegalDetailPanel
          documentoId={selectedDocumentoId}
          onClose={() => setSelectedDocumentoId(null)}
          onEdit={handleEditarDocumento}
          onDelete={handleEliminarDocumento}
          onChangeEstado={handleCambiarEstado}
          onNuevoPago={onNuevoPago}
        />
      )}

      {showModal && (
        <CrearEditarDocumentoLegalModal
          onClose={() => { setShowModal(false); setDocumentoEdit(null); }}
          onCreate={cargarDocumentos}
          documentoEdit={documentoEdit}
        />
      )}
    </>
  );
};

// ============================================================
// CALENDARIO / FLUJO DE CAJA TAB
// ============================================================
const CalendarioFlujoCajaTab = ({ onNuevoPago }) => {
  const [vistaActual, setVistaActual] = useState('calendario');
  const [eventos, setEventos] = useState([]);
  const [datosResumen, setDatosResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const hoy = new Date();
  const enUnMes = new Date();
  enUnMes.setMonth(enUnMes.getMonth() + 1);

  const [fechaDesde, setFechaDesde] = useState(hoy.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(enUnMes.toISOString().split('T')[0]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');

  useEffect(() => { cargarDatos(); }, [vistaActual]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const response = await documentosLegalesService.getFlujoCaja(
        fechaDesde || null, fechaHasta, tipoDocumento || null, estadoFiltro || null, vistaActual
      );
      if (vistaActual === 'calendario') {
        setEventos(response.data.eventos || []);
      } else {
        setDatosResumen(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al cargar el flujo de caja');
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (clickInfo) => {
    const props = clickInfo.event.extendedProps;
    setDocumentoSeleccionado({
      id: props.documento_id,
      numero_documento: props.numero_documento,
      tipo_documento: props.tipo_documento,
      tipo_documento_label: props.tipo_documento_label,
      estado: props.estado,
      estado_label: props.estado_label,
      monto_total: props.monto_total,
      tercero: props.tercero,
      proveedor: props.proveedor_id,
      cliente: props.cliente_id,
      proyecto: props.proyecto,
      descripcion: props.descripcion,
      fecha_emision: props.fecha_emision,
      fecha_vencimiento: props.fecha_vencimiento,
      fecha_pago_programada: props.fecha_pago_programada,
    });
    setShowModal(true);
  };

  const handleMarcarPagado = async () => {
    if (!documentoSeleccionado) return;
    try {
      await documentosLegalesService.cambiarEstado(documentoSeleccionado.id, 'pagada');
      toast.success('Documento marcado como pagado');
      setShowModal(false);
      cargarDatos();
    } catch {
      toast.error('Error al cambiar el estado del documento');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleExportarExcel = async () => {
    if (!datosResumen || vistaActual !== 'lista') { toast.warning('Cambia a vista de lista para exportar'); return; }
    try {
      const XLSX = await import('xlsx');
      const datos = [];
      datosResumen.datos.forEach(dia => {
        dia.documentos.forEach(doc => {
          datos.push({
            'Fecha Pago Programada': doc.fecha_pago_programada || doc.fecha_vencimiento,
            'Fecha Vencimiento': doc.fecha_vencimiento,
            'Numero': doc.numero_documento,
            'Tipo': doc.tipo_documento_label,
            'Tercero': doc.tercero,
            'Proyecto': doc.proyecto || '-',
            'Estado': doc.estado_label,
            'Monto': doc.monto_total,
            'Por Pagar': doc.es_por_pagar ? doc.monto_total : 0,
            'Por Cobrar': !doc.es_por_pagar ? doc.monto_total : 0
          });
        });
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datos);
      XLSX.utils.book_append_sheet(wb, ws, 'Flujo de Caja');
      XLSX.writeFile(wb, `flujo-caja-${fechaDesde}-${fechaHasta}.xlsx`);
      toast.success('Flujo de caja exportado a Excel');
    } catch {
      toast.error('Error al exportar a Excel');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="flujo-caja-tab">
      {/* Header con toggle */}
      <div className="flujo-header">
        <div className="flujo-title-section"></div>
        <div className="vista-toggle">
          <button className={`btn-toggle ${vistaActual === 'calendario' ? 'active' : ''}`} onClick={() => setVistaActual('calendario')}>
            <FiCalendar /> Calendario
          </button>
          <button className={`btn-toggle ${vistaActual === 'lista' ? 'active' : ''}`} onClick={() => setVistaActual('lista')}>
            <FiList /> Lista
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <div className="filtro-group">
          <label><FiCalendar /> Desde:</label>
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="filtro-input" />
        </div>
        <div className="filtro-group">
          <label><FiCalendar /> Hasta:</label>
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="filtro-input" />
        </div>
        <div className="filtro-group">
          <label><FiFilter /> Tipo:</label>
          <select value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)} className="filtro-select">
            <option value="">Todos</option>
            <option value="factura_compra">Factura Compra</option>
            <option value="factura_venta">Factura Venta</option>
            <option value="boleta">Boleta</option>
            <option value="nota_credito">Nota de Credito</option>
            <option value="nota_debito">Nota de Debito</option>
          </select>
        </div>
        <div className="filtro-group">
          <label><FiFilter /> Estado:</label>
          <select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)} className="filtro-select">
            <option value="">Todos</option>
            <option value="emitida">Emitida</option>
            <option value="pagada">Pagada</option>
            <option value="vencida">Vencida</option>
          </select>
        </div>
        <button onClick={cargarDatos} className="btn-filtrar"><FiFilter /> Filtrar</button>
        {vistaActual === 'lista' && (
          <button onClick={handleExportarExcel} className="btn-exportar"><FiDownload /> Exportar</button>
        )}
      </div>

      {/* Resumen KPIs (solo en vista lista) */}
      {vistaActual === 'lista' && datosResumen && (
        <div className="resumen-cards">
          <div className="resumen-card">
            <div className="resumen-label">Total Documentos</div>
            <div className="resumen-value">{datosResumen.resumen.total_documentos}</div>
          </div>
          <div className="resumen-card pagar">
            <div className="resumen-label">Por Pagar</div>
            <div className="resumen-value">{formatCurrency(datosResumen.resumen.total_por_pagar)}</div>
          </div>
          <div className="resumen-card cobrar">
            <div className="resumen-label">Por Cobrar</div>
            <div className="resumen-value">{formatCurrency(datosResumen.resumen.total_por_cobrar)}</div>
          </div>
          <div className={`resumen-card ${datosResumen.resumen.flujo_neto >= 0 ? 'positivo' : 'negativo'}`}>
            <div className="resumen-label">Flujo Neto</div>
            <div className="resumen-value">{formatCurrency(datosResumen.resumen.flujo_neto)}</div>
          </div>
          <div className="resumen-card vencidos">
            <div className="resumen-label">Vencidos</div>
            <div className="resumen-value">{datosResumen.resumen.documentos_vencidos}</div>
            <div className="resumen-sub">{formatCurrency(datosResumen.resumen.total_vencido)}</div>
          </div>
        </div>
      )}

      {/* Vista Calendario */}
      {vistaActual === 'calendario' && (
        <div className="calendario-container">
          <div className="leyenda">
            <div className="leyenda-item"><span className="leyenda-color" style={{ background: '#fd7e14' }}></span><span>Por Pagar</span></div>
            <div className="leyenda-item"><span className="leyenda-color" style={{ background: '#0d6efd' }}></span><span>Por Cobrar</span></div>
            <div className="leyenda-item"><span className="leyenda-color" style={{ background: '#198754' }}></span><span>Pagado</span></div>
            <div className="leyenda-item"><span className="leyenda-color" style={{ background: '#dc3545' }}></span><span>Vencido</span></div>
          </div>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={esLocale}
            events={eventos}
            eventClick={handleEventClick}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,dayGridWeek' }}
            height="auto"
            eventContent={(eventInfo) => (
              <div className="fc-event-content-custom">
                <div className="fc-event-title">{eventInfo.event.title}</div>
                <div className="fc-event-monto">{formatCurrency(eventInfo.event.extendedProps.monto_total)}</div>
              </div>
            )}
          />
        </div>
      )}

      {/* Vista Lista */}
      {vistaActual === 'lista' && datosResumen && (
        <div className="lista-container">
          {datosResumen.datos.map((dia, idx) => (
            <div key={idx} className="dia-grupo">
              <div className="dia-header">
                <h3>{formatDate(dia.fecha)}</h3>
                <div className="dia-totales">
                  <span className="total-pagar">Por Pagar: {formatCurrency(dia.total_pagar)}</span>
                  <span className="total-cobrar">Por Cobrar: {formatCurrency(dia.total_cobrar)}</span>
                  <span className={`flujo-neto ${dia.flujo_neto >= 0 ? 'positivo' : 'negativo'}`}>
                    Flujo: {formatCurrency(dia.flujo_neto)}
                  </span>
                </div>
              </div>
              <div className="documentos-lista">
                {dia.documentos.map((doc) => (
                  <div key={doc.id} className={`documento-card ${doc.es_por_pagar ? 'pagar' : 'cobrar'} ${doc.estado}`}>
                    <div className="documento-info">
                      <div className="documento-header-card">
                        <span className="documento-numero">{doc.numero_documento}</span>
                        <span className={`documento-tipo ${doc.tipo_documento}`}>{doc.tipo_documento_label}</span>
                        <span className={`documento-estado ${doc.estado}`}>{doc.estado_label}</span>
                      </div>
                      <div className="documento-tercero">{doc.tercero}</div>
                      {doc.proyecto && <div className="documento-proyecto">{doc.proyecto}</div>}
                      {doc.fecha_vencimiento && doc.fecha_pago_programada && doc.fecha_vencimiento !== doc.fecha_pago_programada && (
                        <div className="documento-descripcion" style={{ fontSize: '0.75rem', color: '#7c3aed' }}>
                          Venc. original: {formatDate(doc.fecha_vencimiento)}
                        </div>
                      )}
                      {doc.descripcion && <div className="documento-descripcion">{doc.descripcion}</div>}
                    </div>
                    <div className="documento-monto">
                      <div className="monto-label">{doc.es_por_pagar ? 'A Pagar' : 'A Cobrar'}</div>
                      <div className="monto-valor">{formatCurrency(doc.monto_total)}</div>
                      {onNuevoPago && ['emitida', 'vencida'].includes(doc.estado) && (
                        <button
                          className="btn-pagar"
                          style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem', marginTop: '0.35rem', background: '#059669' }}
                          onClick={() => onNuevoPago({
                            id: doc.id,
                            numero_documento: doc.numero_documento,
                            tipo_documento: doc.tipo_documento,
                            monto_total: doc.monto_total,
                            proveedor: doc.proveedor_id,
                            cliente: doc.cliente_id,
                            fecha_pago_programada: doc.fecha_pago_programada,
                            estado: doc.estado,
                          })}
                        >
                          + Pago
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {datosResumen.datos.length === 0 && (
            <div className="no-data">No hay documentos en el periodo seleccionado</div>
          )}
        </div>
      )}

      {/* Modal de Detalle */}
      {showModal && documentoSeleccionado && (
        <div className="fc-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="fc-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="fc-modal-header">
              <h2>Detalle del Documento</h2>
              <button onClick={() => setShowModal(false)} className="btn-icon" style={{ fontSize: '1.25rem' }}>&times;</button>
            </div>
            <div className="fc-modal-body">
              <div className="detalle-row"><span className="detalle-label">Numero:</span><span className="detalle-value">{documentoSeleccionado.numero_documento}</span></div>
              <div className="detalle-row"><span className="detalle-label">Tipo:</span><span className="detalle-value">{documentoSeleccionado.tipo_documento_label}</span></div>
              <div className="detalle-row"><span className="detalle-label">Estado:</span><span className={`estado-badge ${documentoSeleccionado.estado}`}>{documentoSeleccionado.estado_label}</span></div>
              <div className="detalle-row"><span className="detalle-label">Tercero:</span><span className="detalle-value">{documentoSeleccionado.tercero}</span></div>
              {documentoSeleccionado.proyecto && (
                <div className="detalle-row"><span className="detalle-label">Proyecto:</span><span className="detalle-value">{documentoSeleccionado.proyecto}</span></div>
              )}
              <div className="detalle-row"><span className="detalle-label">Fecha Emision:</span><span className="detalle-value">{formatDate(documentoSeleccionado.fecha_emision)}</span></div>
              <div className="detalle-row"><span className="detalle-label">Fecha Vencimiento:</span><span className="detalle-value">{formatDate(documentoSeleccionado.fecha_vencimiento)}</span></div>
              {documentoSeleccionado.fecha_pago_programada && (
                <div className="detalle-row"><span className="detalle-label">Fecha Pago Programada:</span><span className="detalle-value" style={{ color: '#7c3aed', fontWeight: 600 }}>{formatDate(documentoSeleccionado.fecha_pago_programada)}</span></div>
              )}
              <div className="detalle-row"><span className="detalle-label">Monto:</span><span className="detalle-value monto-grande">{formatCurrency(documentoSeleccionado.monto_total)}</span></div>
              {documentoSeleccionado.descripcion && (
                <div className="detalle-row"><span className="detalle-label">Descripcion:</span><span className="detalle-value">{documentoSeleccionado.descripcion}</span></div>
              )}
            </div>
            <div className="fc-modal-footer">
              {onNuevoPago && ['emitida', 'vencida'].includes(documentoSeleccionado.estado) && (
                <button
                  className="btn-pagar"
                  style={{ background: '#059669' }}
                  onClick={() => {
                    setShowModal(false);
                    onNuevoPago(documentoSeleccionado);
                  }}
                >
                  + Nuevo Pago
                </button>
              )}
              {documentoSeleccionado.estado !== 'pagada' && (
                <button onClick={handleMarcarPagado} className="btn-pagar">Marcar como Pagado</button>
              )}
              <button onClick={() => setShowModal(false)} className="btn-cancelar">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN PAGE WITH TABS
// ============================================================
const PAGO_FORM_INITIAL = {
  tipo: 'egreso', cuenta_bancaria: '', metodo_pago: 'transferencia',
  numero_comprobante: '', fecha_pago: '', monto: '', proveedor: '',
  cliente: '', glosa: '', documento_legal: '', monto_aplicado: '',
};

const DIAS_SEMANA = [
  { id: 0, label: 'Lun' },
  { id: 1, label: 'Mar' },
  { id: 2, label: 'Mie' },
  { id: 3, label: 'Jue' },
  { id: 4, label: 'Vie' },
  { id: 5, label: 'Sab' },
  { id: 6, label: 'Dom' },
];

const getDefaultDiasPago = () => {
  try {
    const saved = localStorage.getItem('dias_pago_config');
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return [0, 2, 4]; // Lunes, Miercoles, Viernes
};

const DocumentosLegalesPage = () => {
  const [activeTab, setActiveTab] = useState('gantt');
  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [pagoForm, setPagoForm] = useState(PAGO_FORM_INITIAL);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [documentosPendientes, setDocumentosPendientes] = useState([]);
  const [pagoDataLoaded, setPagoDataLoaded] = useState(false);
  const [savingPago, setSavingPago] = useState(false);
  const [showDiasPagoConfig, setShowDiasPagoConfig] = useState(false);
  const [diasPago, setDiasPago] = useState(getDefaultDiasPago);
  const [recalculando, setRecalculando] = useState(false);

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const handleToggleDiaPago = (diaId) => {
    setDiasPago(prev => {
      const next = prev.includes(diaId) ? prev.filter(d => d !== diaId) : [...prev, diaId].sort();
      localStorage.setItem('dias_pago_config', JSON.stringify(next));
      return next;
    });
  };

  const handleRecalcularFechasPago = async () => {
    if (diasPago.length === 0) {
      toast.warning('Seleccione al menos un dia de pago');
      return;
    }
    try {
      setRecalculando(true);
      const API_BASE_URL = getApiBaseUrl();
      const res = await api.post(`${API_BASE_URL}/documentos-legales/recalcular-fechas-pago/`, {
        dias_pago: diasPago,
      });
      toast.success(`${res.data.documentos_actualizados} documentos actualizados`);
      setShowDiasPagoConfig(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al recalcular fechas de pago');
    } finally {
      setRecalculando(false);
    }
  };

  const cargarDatosPago = async () => {
    if (pagoDataLoaded) return;
    try {
      const [cuentasRes, provRes] = await Promise.all([
        tesoreriaService.cuentasBancarias.getAll({ activo: true }),
        proveedorService.getAll(),
      ]);
      setCuentasBancarias(ensureArray(cuentasRes.data.results || cuentasRes.data));
      setProveedores(ensureArray(provRes.data.results || provRes.data));
      setPagoDataLoaded(true);
    } catch { toast.error('Error al cargar datos para el pago'); }
  };

  const cargarDocumentosProveedor = async (proveedorId) => {
    if (!proveedorId) { setDocumentosPendientes([]); return; }
    try {
      const res = await documentosLegalesService.getAll({
        proveedor: proveedorId,
        tipo_documento__in: 'factura_compra,boleta_honorarios,factura_exenta,nota_debito',
        estado__in: 'emitida,vencida',
      });
      setDocumentosPendientes(ensureArray(res.data.results || res.data));
    } catch { setDocumentosPendientes([]); }
  };

  const handleAbrirPagoModal = async () => {
    setPagoForm(PAGO_FORM_INITIAL);
    setDocumentosPendientes([]);
    await cargarDatosPago();
    setPagoModalOpen(true);
  };

  const handleNuevoPagoDesdeDocumento = async (documento) => {
    await cargarDatosPago();
    const esCompra = ['factura_compra', 'boleta_honorarios', 'factura_exenta', 'nota_debito'].includes(documento.tipo_documento);
    const tipo = esCompra ? 'egreso' : 'ingreso';
    const monto = parseFloat(documento.saldo_pendiente || documento.monto_total || 0);
    const fechaPago = documento.fecha_pago_programada || new Date().toISOString().split('T')[0];

    // Pre-fill form
    const form = {
      ...PAGO_FORM_INITIAL,
      tipo,
      fecha_pago: fechaPago,
      monto: monto,
      glosa: `Pago ${documento.numero_documento}`,
      documento_legal: String(documento.id),
      monto_aplicado: monto,
    };

    if (esCompra && documento.proveedor) {
      form.proveedor = String(documento.proveedor);
      // Load pending documents for this provider
      try {
        const res = await documentosLegalesService.getAll({
          proveedor: documento.proveedor,
          tipo_documento__in: 'factura_compra,boleta_honorarios,factura_exenta,nota_debito',
          estado__in: 'emitida,vencida',
        });
        setDocumentosPendientes(ensureArray(res.data.results || res.data));
      } catch { setDocumentosPendientes([]); }
    } else if (!esCompra && documento.cliente) {
      form.cliente = String(documento.cliente);
    }

    setPagoForm(form);
    setPagoModalOpen(true);
  };

  const handleProveedorChange = (provId) => {
    setPagoForm(prev => ({ ...prev, proveedor: provId, documento_legal: '', monto_aplicado: '' }));
    cargarDocumentosProveedor(provId);
  };

  const handleDocumentoChange = (docId) => {
    const doc = documentosPendientes.find(d => String(d.id) === String(docId));
    if (doc) {
      const saldo = parseFloat(doc.saldo_pendiente || doc.monto_total || 0);
      setPagoForm(prev => ({ ...prev, documento_legal: docId, monto_aplicado: saldo, monto: prev.monto || saldo }));
    } else {
      setPagoForm(prev => ({ ...prev, documento_legal: docId, monto_aplicado: '' }));
    }
  };

  const handleGuardarPago = async () => {
    try {
      setSavingPago(true);
      const data = {
        tipo: pagoForm.tipo,
        cuenta_bancaria: pagoForm.cuenta_bancaria,
        metodo_pago: pagoForm.metodo_pago,
        numero_comprobante: pagoForm.numero_comprobante,
        fecha_pago: pagoForm.fecha_pago,
        monto: pagoForm.monto,
        glosa: pagoForm.glosa,
      };
      if (pagoForm.tipo === 'egreso' && pagoForm.proveedor) data.proveedor = pagoForm.proveedor;
      if (pagoForm.tipo === 'ingreso' && pagoForm.cliente) data.cliente = pagoForm.cliente;
      if (pagoForm.documento_legal && pagoForm.monto_aplicado) {
        data.detalles_pago = [{ documento_legal: pagoForm.documento_legal, monto_aplicado: pagoForm.monto_aplicado }];
      }
      await tesoreriaService.pagos.create(data);
      toast.success('Pago creado exitosamente');
      setPagoModalOpen(false);
      setPagoForm(PAGO_FORM_INITIAL);
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        toast.error(msgs);
      } else {
        toast.error(errData?.detail || 'Error al crear pago');
      }
    } finally {
      setSavingPago(false);
    }
  };

  return (
    <div className="documentos-legales-page">
      <div className="page-header">
        <h1>Documentos</h1>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div className="dias-pago-wrapper">
            <button
              className="btn-dias-pago"
              onClick={() => setShowDiasPagoConfig(!showDiasPagoConfig)}
              title="Configurar dias de pago"
            >
              <FiSettings /> Dias de Pago
            </button>
            {showDiasPagoConfig && (
              <div className="dias-pago-popover">
                <div className="dias-pago-title">Dias de pago programado</div>
                <div className="dias-pago-checks">
                  {DIAS_SEMANA.map(dia => (
                    <label key={dia.id} className={`dia-check ${diasPago.includes(dia.id) ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={diasPago.includes(dia.id)}
                        onChange={() => handleToggleDiaPago(dia.id)}
                      />
                      {dia.label}
                    </label>
                  ))}
                </div>
                <button
                  className="btn-recalcular"
                  onClick={handleRecalcularFechasPago}
                  disabled={recalculando || diasPago.length === 0}
                >
                  {recalculando ? 'Recalculando...' : 'Recalcular Fechas'}
                </button>
              </div>
            )}
          </div>
          <button className="btn-primary" onClick={handleAbrirPagoModal}>
            <FiPlus /> Nuevo Pago
          </button>
        </div>
      </div>

      <div className="docleg-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`docleg-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'gantt' && <GanttRecursosTab />}
      {activeTab === 'documentos' && <DocumentosLegalesTab onNuevoPago={handleNuevoPagoDesdeDocumento} />}
      {activeTab === 'solicitudes' && <SolicitudesTab />}
      {activeTab === 'ordenes' && <OrdenesCompraTab />}
      {activeTab === 'guias' && <GuiasDespachoTab />}
      {activeTab === 'calendario' && <CalendarioFlujoCajaTab onNuevoPago={handleNuevoPagoDesdeDocumento} />}
      {activeTab === 'cronograma' && <CronogramaTab />}

      {/* Modal Nuevo Pago */}
      {pagoModalOpen && (
        <div className="pago-modal-overlay" onClick={() => setPagoModalOpen(false)}>
          <div className="pago-modal" onClick={e => e.stopPropagation()}>
            <div className="pago-modal-header">
              <h3>Nuevo Pago</h3>
              <button className="btn-icon" onClick={() => setPagoModalOpen(false)} title="Cerrar">&times;</button>
            </div>
            <div className="pago-modal-body">
              <div className="pago-form-grid">
                <div className="pago-form-group">
                  <label>Tipo *</label>
                  <select value={pagoForm.tipo} onChange={e => setPagoForm({ ...pagoForm, tipo: e.target.value })}>
                    <option value="egreso">Egreso (Pago a proveedor)</option>
                    <option value="ingreso">Ingreso (Cobro a cliente)</option>
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>Cuenta Bancaria *</label>
                  <select value={pagoForm.cuenta_bancaria} onChange={e => setPagoForm({ ...pagoForm, cuenta_bancaria: e.target.value })}>
                    <option value="">Seleccionar...</option>
                    {cuentasBancarias.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.banco})</option>)}
                  </select>
                </div>
                {pagoForm.tipo === 'egreso' && (
                  <div className="pago-form-group">
                    <label>Proveedor *</label>
                    <select value={pagoForm.proveedor} onChange={e => handleProveedorChange(e.target.value)}>
                      <option value="">Seleccionar proveedor...</option>
                      {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre} {p.rut ? `(${p.rut})` : ''}</option>)}
                    </select>
                  </div>
                )}
                {pagoForm.tipo === 'ingreso' && (
                  <div className="pago-form-group">
                    <label>Cliente *</label>
                    <input type="number" value={pagoForm.cliente} onChange={e => setPagoForm({ ...pagoForm, cliente: e.target.value })} placeholder="ID del cliente" />
                  </div>
                )}
                <div className="pago-form-group">
                  <label>Metodo de Pago *</label>
                  <select value={pagoForm.metodo_pago} onChange={e => setPagoForm({ ...pagoForm, metodo_pago: e.target.value })}>
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="cheque">Cheque</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta_credito">Tarjeta de Credito</option>
                    <option value="tarjeta_debito">Tarjeta de Debito</option>
                    <option value="vale_vista">Vale Vista</option>
                  </select>
                </div>
                <div className="pago-form-group">
                  <label>N° Comprobante</label>
                  <input value={pagoForm.numero_comprobante} onChange={e => setPagoForm({ ...pagoForm, numero_comprobante: e.target.value })} placeholder="N° transferencia, cheque..." />
                </div>
                <div className="pago-form-group">
                  <label>Fecha *</label>
                  <input type="date" value={pagoForm.fecha_pago} onChange={e => setPagoForm({ ...pagoForm, fecha_pago: e.target.value })} />
                </div>
                <div className="pago-form-group">
                  <label>Monto *</label>
                  <input type="number" value={pagoForm.monto} onChange={e => setPagoForm({ ...pagoForm, monto: e.target.value })} placeholder="0" />
                </div>
                {pagoForm.tipo === 'egreso' && pagoForm.proveedor && (
                  <div className="pago-form-group">
                    <label>Documento Asociado</label>
                    <select value={pagoForm.documento_legal} onChange={e => handleDocumentoChange(e.target.value)}>
                      <option value="">Sin documento asociado</option>
                      {documentosPendientes.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.numero_documento} - {formatCurrency(d.saldo_pendiente || d.monto_total)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {pagoForm.documento_legal && (
                  <div className="pago-form-group">
                    <label>Monto Aplicado al Documento</label>
                    <input type="number" value={pagoForm.monto_aplicado} onChange={e => setPagoForm({ ...pagoForm, monto_aplicado: e.target.value })} placeholder="0" />
                  </div>
                )}
                <div className="pago-form-group pago-form-full">
                  <label>Glosa</label>
                  <textarea value={pagoForm.glosa} onChange={e => setPagoForm({ ...pagoForm, glosa: e.target.value })} rows={3} placeholder="Descripcion del pago..." />
                </div>
              </div>
            </div>
            <div className="pago-modal-footer">
              <button className="btn-secondary" onClick={() => setPagoModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleGuardarPago} disabled={savingPago}>
                {savingPago ? 'Creando...' : 'Crear Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosLegalesPage;
